/*jshint bitwise:false */
define('bindingHandlers/columnChart',[
    'jquery',
    'lib/lodash4',
    'lib/knockout',
    'moment',
    'lib/d3',
    'lib/dc',
    'util/dcChartUtils',
    'extensions/i18n/knockout.i18n'
], function (
    $,
    _,
    ko,
    moment,
    d3,
    dc,
    chartUtils
) {
    'use strict';

    // ====================================================================
    // Constants & Variables
    // ====================================================================

    /* Generic chart canvas class */
    var CANVAS_CLASS = "chart-canvas";

    /* Default chart padding */
    var CHART_PADDING = 100;

    /* Unique DOMDATA key used for storing data on DOM element */
    var COLUMN_CHART_DOMDATA_KEY = "column-chart-binding-data";

    /* Right margin, needed to offset chart for legend */
    var RIGHT_MARGIN = 180;

    /* Padding for y-axis */
    var Y_AXIS_LABEL_PADDING = CHART_PADDING / 4;

    /* Maximum number of ticks to draw for a given chart */
    var MAX_TICK_COUNT = 48;

    /* Retained references to tooltip objects, used for disposal */
    var tooltipsReferences = [];

    // ====================================================================
    // Knockout Binding Handler
    // ====================================================================

    /**
     * Creates an instance of the DC.js bar chart using crossfilter.
     *
     * @param {Object} data.dimension - A dimension object created by the
     *  crossfilter library. This provides a specific context used by DC
     *  to break down data passed into the crossfilter object. See
     *  crossfilter documentation for more information on dimensions:
     *  https://github.com/square/crossfilter/wiki/API-Reference#dimension
     *
     * @param {Array} data.groups - An array of groups created from a
     *  given crossfilter dimension. Each group is a subset of data for a
     *  given dimension. Each group will be plotted as an individual bar
     *  chart and composited together. See crossfilter documentation on for
     *  more information on groups:
     *  https://github.com/square/crossfilter/wiki/API-Reference#group-map-reduce
     *
     * @param {Object} data.crossfilter - A crossfilter object created
     *  from a set of data. See crossfilter documentatino for more information
     *  on crossfilter:
     *  https://github.com/square/crossfilter/wiki/API-Reference
     *
     * @param {Array} legend.titles - An array of strings or string keys that
     *  will be used as labels for each group. Groups and titles should have
     *  a 1:1 relationship.
     *
     * @param {Array} xAxis.domain - An array of 2 values representing the
     *  range of the chart's X axis. These values should be either date
     *  objects or numeric, depending on the specified type.
     *
     * @param {String} [xAxis.type=undefined] - The type of the axis. Only
     *  two types are currently supported: time and linear. If the type is
     *  not specified or set to time, a d3 time scale will be used. If any
     *  other type is specified, a linear scale will be used.
     *
     * @param {Array} yAxis.domain - An arry of 2 numeric values representing
     *  the range of the chart's Y axis. These values should be numeric as
     *  the Y scale of the columnChart binding only supports a linear scale.
     *
     * @param {String} yAxis.tickFormat - A string representing the globalize 
     *  number format to use for ticks. Default n1 if the difference in domain
     *  is less than 3, else n0.
     *
     *
     * Example Usage:
     *
     * <div class="chart" data-bind="columnChart: {
     *      data: {
     *          dimension: dateDimension,
     *          groups: columnChartGroups,
     *          crossfilter: crossfilter
     *      },
     *      legend: {
     *          titles: columnChartTitles
     *      },
     *      xAxis: {
     *          type: 'time',
     *          domain: columnChartDomainX
     *      },
     *      yAxis: {
     *          domain: columnChartDomainY
     *      }
     *  }"></div>
     *
     **/
    ko.bindingHandlers.columnChart = {

        init: function (element, valueAccessor) {

            var $element = $(element),
                bindingData = ko.unwrap(valueAccessor()),
                chartWidth = $element.width() - (CHART_PADDING / 2),
                chartHeight = $element.height(),
                chartAspect = chartHeight/chartWidth,
                scale        = d3.scale.ordinal().range(chartUtils.COLORS),
                chartId = _.uniqueId("_column_chart_"),
                chartClass = CANVAS_CLASS + chartId,
                barCharts = [],
                xScale,
                yScale;

            // Throw errors if insufficient data provided to binding

            if (!bindingData.data) {
                throw new Error("Column chart data parameter is required.");
            }

            if (!bindingData.data.dimension) {
                throw new Error("Column chart data.dimension parameter is required.");
            }

            if (!bindingData.data.groups) {
                throw new Error("Column chart data.group parameter is required.");
            }

            if (!bindingData.legend || !bindingData.legend.titles) {
                throw new Error("Column chart legend.titles parameter is required.");
            }

            // Access elements of binding data

            var dateDim         = ko.unwrap(bindingData.data.dimension);
            var groups          = ko.unwrap(bindingData.data.groups);
            var seriesTitles    = ko.unwrap(bindingData.legend.titles);
            var xAxis           = ko.unwrap(bindingData.xAxis);
            var yAxis           = ko.unwrap(bindingData.yAxis);
            var canBrush        = ko.unwrap(bindingData.brush) || false;

            // Prepare DOM for chart

            $element.addClass("chart chart-bar");

            var $canvasNode = $("<div/>", {
                "class": chartClass
            });

            $element.prepend($canvasNode);

            // Process chart data for rendering

            seriesTitles = _.map(seriesTitles, function (t) {
                return ko.i18n.renderString(t);
            });

            // Create composite chart

            var compositeChart = dc.extendedCompositeChart($canvasNode[0]);

            // Create bar charts for each series

            _.each(groups, function(g, index) {

                var barChart = dc.extendedBarChart(compositeChart);

                barChart
                    .group(g,seriesTitles[index])
                    .centerBar(true);

                barCharts.push(barChart);

            });

            // process scales

            var elasticX = false;
            var elasticY = false;
            var xDomain, yDomain;

            if(xAxis && xAxis.type && xAxis.type === "time"){
                if(xAxis.domain){
                    xDomain = ko.unwrap(xAxis.domain);
                    xScale = d3.time.scale().domain([xDomain[0],xDomain[1]]);
                } else {
                    xScale = d3.time.scale();
                    elasticX = true;
                }
            } else {
                if(xAxis && xAxis.domain){
                    xDomain = ko.unwrap(xAxis.domain);
                    xScale = d3.scale.linear().domain([xDomain[0],xDomain[1]]);
                } else {
                    xScale = d3.scale.linear();
                    elasticX = true;
                }
            }

            // yScale only currently supports a linear scale.  A specified type
            // will be used to format the y-axis tick mark.

            if(yAxis && yAxis.domain){
                yDomain = ko.unwrap(yAxis.domain);
                yScale = d3.scale.linear().domain([yDomain[0],yDomain[1]]);
            } else {
                yScale = d3.scale.linear();
            }

            var xAxisLabel, yAxisLabel;
            if (xAxis && xAxis.title) {
                xAxisLabel = ko.i18n.renderString(xAxis.title);
            } else {
                xAxisLabel = "";
            }

            if (yAxis && yAxis.title) {
                yAxisLabel = ko.i18n.renderString(yAxis.title);
            } else {
                yAxisLabel = "";
            }

            var yTickFormat;
            if (yAxis && yAxis.tickFormat) {
                yTickFormat = yAxis.tickFormat;
            } else {
                var domain = yScale.domain();
                yTickFormat = 'n0';
                if (domain && domain.length === 2) {
                    if (domain[1] - domain[0] < 3) {
                        // 3 because our tick count is 4
                        // used to make sure we don't have tick values of 0, 0, 1, 1, 2, ..
                        yTickFormat = 'n1';
                    }
                }
            }

            function disposeTooltips(){
                _.each(tooltipsReferences, function(tt) {
                    tt.dispose();
                });
            }

            function generateTooltips(chart){
                try {
                    var tip = chartUtils.createTooltip();

                    var chartGranularity = chartUtils.getGranularity(moment(xAxis.domain()[0]), moment(xAxis.domain()[1]));
                    var scaleType = ko.unwrap(yAxis.type);

                    var showTooltip = _.partial(chartUtils.showBasicTooltip, tip, chart, chartGranularity, scaleType);
                    var hideTooltip = _.partial(chartUtils.hideBasicTooltip, tip);

                    chart.svg().call(tip);
                    chart.svg().selectAll(".bar").on('mouseover', showTooltip);
                    chart.svg().selectAll(".bar").on('mouseout', hideTooltip);

                    tooltipsReferences.push(tip);
                } catch(err) { /* noop */ }
            }

            // Set all properties on chart object

            compositeChart

                .width(chartWidth)
                .height(chartHeight)

                .dimension(dateDim)
                .transitionDuration(500)

                .margins({
                    top: CHART_PADDING/2,
                    right: RIGHT_MARGIN,
                    bottom: CHART_PADDING/2,
                    left: CHART_PADDING
                })

                // set color space

                .colors(scale)
                .shareColors(true)
                .renderTitle(false)

                // set scale & units

                .elasticX(elasticX)
                .elasticY(elasticY)
                .x(xScale)
                .y(yScale)
                .xUnits(function() {
                    return groups[0].size()*(groups.length+1); // how many data points do we have
                })
                .xAxisLabel(xAxisLabel)
                .yAxisLabel(yAxisLabel, Y_AXIS_LABEL_PADDING)
                .round(d3.time.hours.round) // this will help the range selector extend on a per unit basis


                .xUnits(function() {
                    return groups[0].size()*groups.length; // how many data points do we have
                })

                // rendering options

                .renderHorizontalGridLines(true)
                .brushOn(canBrush)

                // compose chart with bar charts

                .compose(barCharts)

                .on("zoomed", function(chart){
                    disposeTooltips();
                    generateTooltips(chart);
                })

                .on("preRedraw", disposeTooltips)
                .on("preRender", disposeTooltips)

                // adjustments made just after a redraw/rerender

                .renderlet(function (chart) {

                    var gap = 2;

                    var bars = chart.selectAll('.sub._0 rect.bar');
                    var firstBar = bars[0][0];
                    var seriesCount = groups.length;

                    if(firstBar){
                        var barWidth = parseInt(d3.select(firstBar).attr('width'), 10) || 1;
                        var position = -1 * ( barWidth + gap );

                        for(var i = 0; i < seriesCount; i++) {
                            chart.selectAll("g.sub._"+i)
                                .attr("transform", "translate(" + position + ",0)");
                            position = position + ( barWidth + gap );
                        }

                    }

                    // Activate Tooltips

                    generateTooltips(chart);

                    // push bar layers above brushing layer so they register mouseover/out events

                    var gContainer = compositeChart.select('g');
                    var chartBars = compositeChart.selectAll('.sub');

                    _.each(chartBars[0], function(bar) {

                        bar.remove();
                        gContainer.append(function () {
                            return bar;
                        });

                    });

                })

                // Focus all other charts on the filtered date range when the filtered
                // event is fired.

                .on('filtered', function (chart,filter) {

                    // IDs of charts that shouldn't be focused
                    var chartsNotToFocus = [];

                    // All currently registered charts in DC
                    var charts = dc.chartRegistry.list();

                    function shouldFocus(chart) {
                        return !_.includes(chartsNotToFocus,chart.chartID());
                    }

                    function focusChart(chart) {
                        if(filter){
                            chart.focus([filter[0],filter[1]]);
                        } else {
                            chart.focus();
                        }
                    }

                    if(canBrush){

                        if(chart.children() && chart.children.length > 0){
                            chartsNotToFocus = _.invokeMap(chart.children(), 'chartID');
                        }

                        chartsNotToFocus.push(compositeChart.chartID());

                        _(charts)
                            .filter(shouldFocus)
                            .each(focusChart);

                    }

                });

            // Format x and y acix (tick labels)

            var tickCount = groups[0].size();
            var currentTick = 0;

            tickCount = Math.min(tickCount, MAX_TICK_COUNT);

            compositeChart.xAxis().ticks(tickCount).tickFormat(function (datum) {

                var format, granularity;

                tickCount = Math.min(compositeChart.xAxis().ticks()[0], MAX_TICK_COUNT);
                if(_.isUndefined(xAxis) || _.isUndefined(xAxis.type) || xAxis.type === "time"){
                    granularity = chartUtils.getGranularity(moment(xAxis.domain()[0]), moment(xAxis.domain()[1]));
                    format = chartUtils.formatChartDate(datum, granularity);
                } else {
                    format = datum;
                }

                if(tickCount > 24){
                    return ((++currentTick)%8 === 1) ? format : "";
                } else if(tickCount > 12) {
                    return ((++currentTick)%4 === 0) ? format : "";
                } else if(tickCount > 6) {
                    return ((++currentTick)%2 === 0) ? format : "";
                } else {
                    return format;
                }

            });

            compositeChart.yAxis().ticks(4).tickFormat(function (datum) {
                return ko.i18n.format(datum, yTickFormat);
            });

            // Render the composite chart

            compositeChart.render();

            // Render the legend

            var svg = d3.select(element).select("svg");
            scale.domain(seriesTitles);

            var legendBinding = {
                "colors" : scale,
                "titles": seriesTitles,
                "svg": svg,
                "width": $element.width(),
                "groups": groups,
                "chart": compositeChart
            };

            ko.bindingHandlers.chartLegend.init(element, function() { return legendBinding; });

            // Store the column chart instance to update later

            chartUtils.setDomData(COLUMN_CHART_DOMDATA_KEY, element, {
                 "columnChart": compositeChart
            });

            // Event Handler (resizing)

            $(window).on("resize.columnChart"+chartId, _.throttle(function () {

                var chart = chartUtils.getDomDataValue(COLUMN_CHART_DOMDATA_KEY, element, "columnChart") || compositeChart;
                var width = $(element).width() - (CHART_PADDING / 2);
                var height = _.max([_.min([chartAspect*width, 300]), 200]);

                if(width > 0 && height > 0){

                    $element.css("height",height+"px");

                    chart.resize(width, height);

                    // TODO: We'll also need to scale/position the brush to roughly the
                    // same position as before the resize.

                    var svg = d3.select(element).select("svg");
                    scale.domain(seriesTitles);

                    ko.bindingHandlers.chartLegend.update(element, function () {
                        return {
                            "colors": scale,
                            "titles": seriesTitles,
                            "svg": svg,
                            "width": $element.width(),
                            "groups": groups,
                            "chart": compositeChart
                        };
                    });

                }

            }, 300));

            // Clean up on DOM node disposal

            ko.utils.domNodeDisposal.addDisposeCallback(element, function () {
                dc.deregisterChart(compositeChart);
                $(window).off(".columnChart"+chartId);
                _.each(tooltipsReferences, function(tt) {
                    tt.dispose();
                });
            });

        },

        update: function (element, valueAccessor) {

            // This assumes that that the chart data structure doesn't change. If datasets are added
            // removed, then a complete render would be necessary, but there doesn't seem to be a use
            // case for that scenario as of now (12/9/14).

            var bindingData     = ko.unwrap(valueAccessor());
            var compositeChart  = chartUtils.getDomDataValue(COLUMN_CHART_DOMDATA_KEY, element, "columnChart");

            // Unwrap crossfilter object to create dependency

            ko.unwrap(bindingData.data.crossfilter);

            // Redraw the chart

            compositeChart.redraw();

        }

    };

});

